
import { UploadProgress, UploadStatus as UploadStatusEnum } from '@/types';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface UploadStatusProps {
  uploadProgress: UploadProgress;
}

const UploadStatus = ({ uploadProgress }: UploadStatusProps) => {
  const { status, progress, fileName, message, error } = uploadProgress;

  if (status === UploadStatusEnum.IDLE) {
    return null;
  }

  if (status === UploadStatusEnum.ERROR) {
    return (
      <Alert variant="destructive" className="my-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error || 'An unknown error occurred'}</AlertDescription>
      </Alert>
    );
  }

  if (status === UploadStatusEnum.SUCCESS) {
    return (
      <Alert variant="default" className="bg-green-50 border-green-200 text-green-800 my-4">
        <CheckCircle2 className="h-4 w-4 text-green-500" />
        <AlertTitle>Success</AlertTitle>
        <AlertDescription>
          {message || `File "${fileName}" was uploaded successfully`}
        </AlertDescription>
      </Alert>
    );
  }

  if (status === UploadStatusEnum.SELECTED) {
    return (
      <Alert variant="default" className="bg-blue-50 border-blue-200 text-blue-800 my-4">
        <Clock className="h-4 w-4 text-blue-500" />
        <AlertTitle>Ready to upload</AlertTitle>
        <AlertDescription>
          {fileName && `Selected file: ${fileName}`}
        </AlertDescription>
      </Alert>
    );
  }

  if (status === UploadStatusEnum.UPLOADING) {
    return (
      <div className="my-4 space-y-2">
        <Alert variant="default" className="bg-blue-50 border-blue-200 text-blue-800">
          <Clock className="h-4 w-4 text-blue-500" />
          <AlertTitle>Uploading...</AlertTitle>
          <AlertDescription>
            {fileName && `Uploading file: ${fileName}`}
          </AlertDescription>
        </Alert>
        <div className="mt-2">
          <div className="flex justify-between text-sm mb-1">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>
    );
  }

  return null;
};

export default UploadStatus;
